import type { ComponentProps } from 'react';
import React from 'react';
import { Link } from '@react-navigation/native';
import { Link as ExpoLink } from 'expo-router';
import { Platform, StyleSheet, View } from 'react-native';
import Animated, { useAnimatedStyle, useDerivedValue, withTiming } from 'react-native-reanimated';

import { BodyText, Icon, TagLabel, theme, usePressableState, useResponsive } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { type AccountTabStackParamList } from '../../../../../navigation/AppNavigation.props';

// ────────────────────────────────────────────────────────────────────────────────

export const AccountMenuLink = (props: AccountMenuLinkProps) => {
  const { testID, label, iconName, active, to: target, href, tag } = props;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  const ref = React.useRef(null);
  const { currentBreakpoint, match } = useResponsive();
  const { isHovered } = usePressableState(ref);

  const shouldShowIcon = !currentBreakpoint.isSM;
  const iOsOnlyRowStyle = Platform.OS === 'ios' ? styles.rowIos : {};

  // ─── Styles ──────────────────────────────────────────────────────────

  const rowStyles = [styles.row, iOsOnlyRowStyle];

  // ─── Animated Styles ─────────────────────────────────────────────────

  const hoverOverlayOpacitySV = useDerivedValue(() => {
    return withTiming(isHovered || active ? 1 : 0);
  });

  const hoverOverlayAnimatedStyle = useAnimatedStyle(() => {
    return {
      opacity: hoverOverlayOpacitySV.value,
    };
  });

  // ─────────────────────────────────────────────────────────────────────

  if (isExpoRouterEnabled && href) {
    return (
      <ExpoLink href={href} testID={testID} accessibilityRole="link" accessibilityLabel={label}>
        <Animated.View style={[styles.hoverOverlay, hoverOverlayAnimatedStyle]} />
        <View ref={ref} style={rowStyles}>
          {shouldShowIcon ? (
            <View style={styles.iconWrapper}>
              <Icon name={iconName} />
            </View>
          ) : null}

          <BodyText size={match([2, 3])}>{label}</BodyText>

          {tag ? (
            <View style={styles.tagContainer}>
              <TagLabel size={3} palette={tag.palette}>
                {tag.title}
              </TagLabel>
            </View>
          ) : null}
        </View>
      </ExpoLink>
    );
  }

  return (
    <Link to={target} testID={testID} accessibilityRole="link" accessibilityLabel={label}>
      <Animated.View style={[styles.hoverOverlay, hoverOverlayAnimatedStyle]} />
      <View ref={ref} style={rowStyles}>
        {shouldShowIcon ? (
          <View style={styles.iconWrapper}>
            <Icon name={iconName} />
          </View>
        ) : null}

        <BodyText size={match([2, 3])}>{label}</BodyText>

        {tag ? (
          <View style={styles.tagContainer}>
            <TagLabel size={3} palette={tag.palette}>
              {tag.title}
            </TagLabel>
          </View>
        ) : null}
      </View>
    </Link>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  hoverOverlay: {
    position: 'absolute',
    top: 2,
    right: -10,
    bottom: 2,
    left: -10,
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.OPACITY.KALE.ALMOST_TRANSPARENT,
    opacity: 0,
  },
  row: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing['4'],
  },
  rowIos: {
    // Because iOS does not handle nested elements well in the 'Link' component,
    // we must remove extra space.
    marginBottom: -6,
  },
  iconWrapper: {
    paddingRight: theme.spacing['4'],
  },
  tagContainer: {
    marginLeft: theme.spacing['2'],
  },
});

// ────────────────────────────────────────────────────────────────────────────────

type AccountMenuLinkProps = Readonly<{
  to: React.ComponentProps<
    typeof Link<ReactNavigation.RootParamList & AccountTabStackParamList>
  >['to'];
  href?: string;
  label: string;
  iconName: ComponentProps<typeof Icon>['name'];
  testID?: string;
  active?: boolean;
  tag?: {
    title: string;
    palette: ComponentProps<typeof TagLabel>['palette'];
  };
}>;
